/**
 * Currency Context - Global currency settings management
 */
import React, { createContext, useContext, useEffect, useState } from 'react';
import { setCurrencySettings, getCurrencySettings } from '../utils/currencyFormatter';
import companySettingsService from '../services/companySettingsService';

const CurrencyContext = createContext();

export const useCurrency = () => {
  const context = useContext(CurrencyContext);
  if (!context) {
    throw new Error('useCurrency must be used within a CurrencyProvider');
  }
  return context;
};

export const CurrencyProvider = ({ children }) => {
  const [currencySettings, setCurrencySettingsState] = useState(getCurrencySettings());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Load currency settings on mount
  useEffect(() => {
    loadCurrencySettings();
  }, []);

  const loadCurrencySettings = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const settings = await companySettingsService.getSettings();
      
      const currencyConfig = {
        currencyCode: settings.currencyCode || 'USD',
        currencySymbol: settings.currencySymbol || '₹',
        decimalPlaces: settings.decimalPlaces || 2,
        thousandSeparator: settings.thousandSeparator || ',',
        decimalSeparator: settings.decimalSeparator || '.',
        languageCode: settings.languageCode || 'en-US'
      };
      
      // Update global currency settings
      setCurrencySettings(currencyConfig);
      setCurrencySettingsState(currencyConfig);
      
    } catch (err) {
      console.error('Failed to load currency settings:', err);
      setError(err.message);
      // Keep default settings on error
    } finally {
      setLoading(false);
    }
  };

  const updateCurrencySettings = (newSettings) => {
    setCurrencySettings(newSettings);
    setCurrencySettingsState(newSettings);
  };

  const refreshCurrencySettings = () => {
    loadCurrencySettings();
  };

  const value = {
    currencySettings,
    loading,
    error,
    updateCurrencySettings,
    refreshCurrencySettings
  };

  return (
    <CurrencyContext.Provider value={value}>
      {children}
    </CurrencyContext.Provider>
  );
};

export default CurrencyContext;
